import React, { useState, useContext, useCallback } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import Close from '../../../../assets/Close';
import { useNavigate } from 'react-router-dom';
import { useGetCategories } from '../../../../hooks/use-get-categories';
import { StepProps } from '../addNewProduct.interface';
import {
	ProductContext,
	ProductProps,
} from '../../../../context/DatabaseProductContext/database-product.context';
import NewAddPlus from '../../../../assets/NewAddPlus';
import AddEditGroupCategory from '../AddEditGroupCategory';
import { Tooltip } from 'react-tooltip';
import { Category } from '../../../../interfaces/category.interface';
import EditPenIcon from '../../../../assets/EditPenIcon';
import NewTrash from '../../../../assets/NewTrash';
import ModalDeleteProduct from '../../ProductDatabase/components/ProductDetailsDatabase/components/ModalDeleteProduct';
import { CategoriesService } from '../../../../services/categories.services';

const ProductCategoryStep: React.FC<StepProps> = ({ setStep }) => {
	const { t } = useTranslate();
	const navigate = useNavigate();
	const { product, setProduct } = useContext(ProductContext);
	const [refresh, setRefresh] = useState<number>(0);
	const [categories] = useGetCategories(
		{
			parentIds: [product?.productGroup!],
			countryCode: product?.countryCode,
		},
		'admin',
		refresh,
	);
	const [openNewCategory, setOpenNewCategory] = useState<boolean>(false);
	const [activeCategory, setActiveCategory] = useState<Category | undefined>(
		undefined,
	);
	const [editCategory, setEditCategory] = useState<boolean>(false);
	const [openDelete, setOpenDelete] = useState<boolean>(false);

	const refreshCategories = useCallback(() => {
		setRefresh((prev) => prev + 1);
	}, []);

	const handleNextStep = () => {
		setProduct({
			...product,
			categoryId: activeCategory?.id,
		} as ProductProps);

		setStep((prevStep) => prevStep + 1);
	};

	const handleClose = () => {
		setOpenDelete(false);
		refreshCategories();
		setOpenNewCategory(false);
		setEditCategory(false);
		setActiveCategory(undefined);
	};

	const handleEditCategory = (category: Category) => {
		setOpenNewCategory(true);
		setEditCategory(true);
		setActiveCategory(category);
	};

	const handleDeleteCategory = (category: Category) => {
		setOpenDelete(true);
		setActiveCategory(category);
	};

	const deleteCategory = async (category: Category) => {
		try {
			await CategoriesService.deleteCategory(category.id);
			handleClose();
		} catch (e) {}
	};

	return (
		<>
			<div className="w-full h-full flex items-center justify-center">
				<main className="min-w-[300px] h-[760px] 2xl:h-auto flex flex-col gap-6 pr-2 2xl:pr-0 overflow-x-hidden overflow-y-scroll 2xl:overflow-auto">
					<section className="flex justify-between items-center">
						<SypacText variant="body-normal-medium">
							<p className="text-xl text-gray-80">
								<T keyName="productGroup.manageProduct">
									Manage product groups and categories
								</T>
							</p>
						</SypacText>
						<SypacButton variant="secondary" size="medium">
							<button
								type="button"
								onClick={() => navigate('/product-database')}
								className="p-2 border border-solid border-gray-10 transition hover:border-primary-violet"
							>
								<Close />
							</button>
						</SypacButton>
					</section>

					<section className="flex items-center justify-between">
						<SypacText variant="body-normal-medium">
							<p className="text-gray-60">
								<T keyName="selectProductStore.selectCategory">
									Select a category to edit.
								</T>
							</p>
						</SypacText>
						<SypacButton variant="subTitle">
							<button
								type="button"
								className="flex gap-3 items-center p-0 bg-transparent group"
								onClick={() => {
									setOpenNewCategory(true);
									setEditCategory(false);
									setActiveCategory(undefined);
								}}
							>
								<NewAddPlus hoverEffect={true} />
								<SypacText variant="body-regular-medium">
									<p className="text-primary-violet mt-[2px] transition group-hover:text-primary-violet/70">
										<T keyName="selectProductCategory.createNewCategory">
											Create new category
										</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
					</section>
					<div className="border-0 border-solid border-t-[1px] border-gray-10" />

					{!openNewCategory ? (
						<div>
							<div
								className={`h-[300px] xl:h-auto grid gap-[20px] grid-cols-${
									categories?.items.length! < 4 ? categories?.items.length : 3
								} xl:grid-cols-${
									categories?.items.length! < 4 ? categories?.items.length : 4
								} pr-3 xl:pr-0 overflow-x-hidden overflow-y-scroll xl:overflow-visible`}
							>
								{categories?.items
									.sort((a, b) => a.name.localeCompare(b.name))
									.map((category) => (
										<div
											key={category.id}
											className={`w-fit flex flex-col gap-3 p-[10px] border border-solid rounded-xl cursor-pointer transition ${
												activeCategory?.id === category.id
													? 'border-cornflower-blue'
													: 'border-gray-10 hover:border-gray-30'
											}`}
											onClick={() => setActiveCategory(category)}
										>
											<div
												style={{
													backgroundImage: `url(${category.imageUrl})`,
												}}
												className="relative w-[204px] h-[128px] xl:w-[248px] xl:h-[174px] bg-no-repeat bg-cover bg-center border border-solid border-gray-10 rounded-10 box-border"
												role="img"
												aria-label={category.name}
											/>

											<section className="flex flex-col gap-3">
												<span className="flex items-center justify-between">
													<SypacText variant="body-regular-medium">
														<p className="w-[126px] xl:w-[184px] truncate text-gray-80">
															{category.name}
														</p>
													</SypacText>

													<div className="flex justify-center gap-[10px]">
														<SypacButton variant="secondary" size="small">
															<button
																type="button"
																className="w-[24px] h-[24px] flex justify-between items-center border-0 bg-gray-10 rounded-lg m-0 p-0 transition hover:bg-gray-20"
																onClick={() => handleEditCategory(category)}
																data-tooltip-id="category-card"
																data-tooltip-content={t(
																	'productGroup.editCategory',
																	'Edit category',
																)}
															>
																<span className="flex scale-[0.8] ml-[1px]">
																	<EditPenIcon color="#454545" />
																</span>
															</button>
														</SypacButton>
														{category.countPrododucts === 0 && (
															<SypacButton variant="secondary" size="small">
																<button
																	type="button"
																	className="w-[24px] h-[24px] flex justify-between items-center border-0 bg-red rounded-lg m-0 p-0 transition hover:bg-red/60"
																	onClick={() => handleDeleteCategory(category)}
																	data-tooltip-id="category-card"
																	data-tooltip-content={t(
																		'productGroup.deleteCategory',
																		'Delete category',
																	)}
																>
																	<span className="flex ml-[3.5px]">
																		<NewTrash color="#FFFFFF" />
																	</span>
																</button>
															</SypacButton>
														)}
													</div>
												</span>
												<SypacText variant="body-regular-medium">
													<p className="text-sm text-gray-40">
														{category.countPrododucts}{' '}
														<T keyName="clientSelectCategory.productsInThisCategory">
															products in this category
														</T>
													</p>
												</SypacText>
											</section>
										</div>
									))}
							</div>

							<nav className="flex justify-center w-full mt-[40px] gap-3">
								<SypacButton
									variant="secondary"
									size="small"
									className="w-full"
								>
									<button
										type="button"
										className="w-full px-6 py-[10px] rounded-lg"
										onClick={() => setStep((prevStep) => prevStep - 1)}
									>
										<SypacText variant="body-regular-medium">
											<p>
												<T keyName="companyDetails.back">Back</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
								<SypacButton variant="primary" size="small" className="w-full">
									<button
										type="button"
										className="w-full px-6 py-[11px] rounded-lg"
										onClick={handleNextStep}
										disabled={!activeCategory}
									>
										<SypacText
											variant="body-regular-medium"
											className="flex items-center gap-4"
										>
											<p>
												<T keyName="clientSelectQuantity.next">Next</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
							</nav>
						</div>
					) : (
						<AddEditGroupCategory
							current={editCategory ? activeCategory : undefined}
							type="category"
							onClose={handleClose}
						/>
					)}
				</main>
			</div>
			<ModalDeleteProduct
				isOpen={openDelete}
				onClose={() => setOpenDelete(false)}
				handleDelete={() => {
					deleteCategory(activeCategory!).then(() => {});
				}}
				group={activeCategory}
			/>
			<Tooltip
				place="top"
				id="category-card"
				style={{
					backgroundColor: '#E8E8E8',
					color: '#000000',
				}}
			/>
		</>
	);
};
export default ProductCategoryStep;
